import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'

interface SectionHeadingProps {
    id: string
    title: string
    eyebrow?: string
    description?: string
    action?: {
        label: string
        href: string
    }
    className?: string
}

export const SectionHeading: FC<SectionHeadingProps> = ({
    id,
    title,
    eyebrow,
    description,
    action,
    className
}): ReactNode => {
    return (
        <div className={cn('flex flex-col gap-5 border-b border-border pb-7 sm:flex-row sm:items-end sm:justify-between', className)}>
            <div className='max-w-2xl'>
                {eyebrow && (
                    <p className='text-xs font-semibold uppercase tracking-[0.18em] text-brand'>
                        {eyebrow}
                    </p>
                )}

                <h2 id={id} className='mt-3 font-heading text-3xl font-medium tracking-tight text-balance sm:text-[2.6rem] sm:leading-[1.1]'>
                    {title}
                </h2>

                {description && (
                    <p className='mt-3 text-base leading-relaxed text-muted-foreground'>
                        {description}
                    </p>
                )}
            </div>

            {action && (
                <Link
                    href={action.href}
                    className='group inline-flex shrink-0 items-center gap-2 text-sm font-semibold text-foreground transition-colors hover:text-brand focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring'
                >
                    {action.label}
                    <ArrowRight className='size-4 transition-transform duration-300 group-hover:translate-x-1' aria-hidden='true' />
                </Link>
            )}
        </div>
    )
}
